import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

interface AnnotationItem {
  id: string
  key: string
  value: string
}

interface ServiceConfigData {
  type: string
  port: number
  targetPort: number
  nodePort?: number
  loadBalancerIP: string
  annotations: Record<string, string>
}

interface ServiceConfigProps {
  value: ServiceConfigData
  onChange: (config: ServiceConfigData) => void
  disabled?: boolean
  className?: string
}

export default function ServiceConfig({
  value,
  onChange,
  disabled = false,
  className = ''
}: ServiceConfigProps) {
  const { t } = useTranslation('common')
  const [isExpanded, setIsExpanded] = useState(false)
  const [annotationItems, setAnnotationItems] = useState<AnnotationItem[]>([])

  // Record<string, string> to AnnotationItem[]
  const convertToAnnotationItems = (annotations: Record<string, string>): AnnotationItem[] => {
    return Object.entries(annotations).map(([key, annotationValue], index) => ({
      id: `annotation-${Date.now()}-${index}`,
      key,
      value: annotationValue
    }))
  }

  // AnnotationItem[] back to Record<string, string>
  const convertToAnnotationsRecord = (items: AnnotationItem[]): Record<string, string> => {
    const record: Record<string, string> = {}
    items.forEach(item => {
      if (item.key.trim()) {
        record[item.key] = item.value
      }
    })
    return record
  }

  // Seed annotationItems from the incoming value
  useEffect(() => {
    const items = convertToAnnotationItems(value.annotations)
    setAnnotationItems(items)
  }, [])

  const handleInputChange = (field: keyof ServiceConfigData, newValue: any) => {
    onChange({
      ...value,
      [field]: newValue
    })
  }

  const updateAnnotations = (updatedItems: AnnotationItem[]) => {
    setAnnotationItems(updatedItems)
    const newAnnotations = convertToAnnotationsRecord(updatedItems)
    handleInputChange('annotations', newAnnotations)
  }

  const updateAnnotationKey = (id: string, newKey: string) => {
    const updatedItems = annotationItems.map(item =>
      item.id === id ? { ...item, key: newKey } : item
    )
    updateAnnotations(updatedItems)
  }

  const updateAnnotationValue = (id: string, newValue: string) => {
    const updatedItems = annotationItems.map(item =>
      item.id === id ? { ...item, value: newValue } : item
    )
    updateAnnotations(updatedItems)
  }

  const addAnnotation = () => {
    const newItem: AnnotationItem = {
      id: `annotation-${Date.now()}-${Math.random()}`,
      key: '',
      value: ''
    }
    const updatedItems = [...annotationItems, newItem]
    updateAnnotations(updatedItems)
  }

  const removeAnnotation = (id: string) => {
    const updatedItems = annotationItems.filter(item => item.id !== id)
    updateAnnotations(updatedItems)
  }

  const ChevronDownIcon = () => (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
    </svg>
  )

  const ServiceIcon = () => (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M7 16h.01" />
    </svg>
  )

  const PlusIcon = () => (
    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  )

  const TrashIcon = () => (
    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  )

  return (
    <div className={`border border-secondary-300 rounded-lg bg-background ${className}`}>
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-secondary-50 transition-colors duration-200"
        onClick={() => !disabled && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center">
              <ServiceIcon />
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-text-dark">
              Service Configuration
            </h3>
            <p className="text-xs text-text-dark/60">
              {value.type} service on port {value.port}
            </p>
          </div>
        </div>

        {/* Expand/Collapse Arrow */}
        <div
          className={`text-text-dark/40 transition-transform duration-200 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        >
          <ChevronDownIcon />
        </div>
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-secondary-200 bg-secondary-50/30">
          <div className="pt-4 space-y-4">
            {/* Service Type */}
            <div>
              <label className="block text-xs font-medium text-text-dark mb-2">
                Service Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {['ClusterIP', 'NodePort', 'LoadBalancer'].map((serviceType) => (
                  <button
                    key={serviceType}
                    type="button"
                    onClick={() => handleInputChange('type', serviceType)}
                    disabled={disabled}
                    className={`p-3 border-2 rounded-lg text-left transition-all duration-200 ${
                      value.type === serviceType
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        value.type === serviceType ? 'border-primary' : 'border-gray-300'
                      }`}>
                        {value.type === serviceType && (
                          <div className="w-2.5 h-2.5 rounded-full bg-primary"></div>
                        )}
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{serviceType}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Port and Target Port */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-text-dark mb-2">
                  Port <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={value.port}
                  onChange={(e) => handleInputChange('port', parseInt(e.target.value) || 1880)}
                  onWheel={(e) => e.currentTarget.blur()}
                  disabled={disabled}
                  placeholder="1880"
                  className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-lg bg-background text-text-dark placeholder-text-dark/50 focus:ring-2 focus:ring-primary-200 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-dark mb-2">
                  Target Port <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={value.targetPort}
                  onChange={(e) => handleInputChange('targetPort', parseInt(e.target.value) || 1880)}
                  onWheel={(e) => e.currentTarget.blur()}
                  disabled={disabled}
                  placeholder="1880"
                  className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-lg bg-background text-text-dark placeholder-text-dark/50 focus:ring-2 focus:ring-primary-200 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                />
              </div>
            </div>

            {/* NodePort (only show when type is NodePort) */}
            {value.type === 'NodePort' && (
              <div>
                <label className="block text-xs font-medium text-text-dark mb-2">
                  NodePort
                </label>
                <input
                  type="number"
                  value={value.nodePort || ''}
                  onChange={(e) => handleInputChange('nodePort', e.target.value ? parseInt(e.target.value) : undefined)}
                  onWheel={(e) => e.currentTarget.blur()}
                  disabled={disabled}
                  placeholder="30000-32767 (optional)"
                  min="30000"
                  max="32767"
                  className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-lg bg-background text-text-dark placeholder-text-dark/50 focus:ring-2 focus:ring-primary-200 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                />
                <p className="mt-1 text-xs text-text-dark/60">
                  Leave empty for automatic assignment
                </p>
              </div>
            )}

            {/* LoadBalancer IP (only show when type is LoadBalancer) */}
            {value.type === 'LoadBalancer' && (
              <div>
                <label className="block text-xs font-medium text-text-dark mb-2">
                  LoadBalancer IP
                </label>
                <input
                  type="text"
                  value={value.loadBalancerIP}
                  onChange={(e) => handleInputChange('loadBalancerIP', e.target.value)}
                  disabled={disabled}
                  placeholder="192.168.1.100 (optional)"
                  className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-lg bg-background text-text-dark placeholder-text-dark/50 focus:ring-2 focus:ring-primary-200 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                />
                <p className="mt-1 text-xs text-text-dark/60">
                  Leave empty for automatic assignment
                </p>
              </div>
            )}

            {/* Service Annotations */}
            <div className="bg-background rounded-lg border border-secondary-200 p-3">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <label className="text-xs font-medium text-text-dark">
                    Service Annotations
                  </label>
                  <p className="text-xs text-text-dark/60 mt-1">
                    Add custom annotations for service configuration
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addAnnotation}
                  disabled={disabled}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <PlusIcon />
                  Add Annotation
                </button>
              </div>

              {annotationItems.length > 0 ? (
                <div className="space-y-2">
                  {annotationItems.map((item) => (
                    <div key={item.id} className="bg-secondary-50/50 rounded-lg p-3 border border-secondary-200/50">
                      <div className="flex gap-3 items-center">
                        <div className="flex-1">
                          <input
                            type="text"
                            value={item.key}
                            onChange={(e) => updateAnnotationKey(item.id, e.target.value)}
                            disabled={disabled}
                            placeholder="service.beta.kubernetes.io/aws-load-balancer-type"
                            className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-lg bg-background text-text-dark placeholder-text-dark/50 focus:ring-2 focus:ring-primary-200 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                          />
                        </div>
                        <div className="flex-1">
                          <input
                            type="text"
                            value={item.value}
                            onChange={(e) => updateAnnotationValue(item.id, e.target.value)}
                            disabled={disabled}
                            placeholder="nlb"
                            className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-lg bg-background text-text-dark placeholder-text-dark/50 focus:ring-2 focus:ring-primary-200 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAnnotation(item.id)}
                          disabled={disabled}
                          className="flex items-center justify-center w-9 h-9 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                          title={t('flowConfig.removeAnnotation')}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-6 border border-dashed border-secondary-300 rounded-lg bg-secondary-50/30">
                  <div className="w-8 h-8 text-text-dark/30 mb-2">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
                    </svg>
                  </div>
                  <p className="text-xs text-text-dark/60 text-center font-medium">{t('flowConfig.noAnnotations')}</p>
                  <p className="text-xs text-text-dark/40 text-center mt-1">Click "Add Annotation" to get started</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}